/**
 * Age × Reason — customer discovery survey
 *
 * Stacked bar of survey respondents per age bucket (q8_age_range), split by the
 * four reason themes (q2_reason_tried).
 *
 * Built entirely client-side from /api/admin/survey/responses, which already
 * returns every response flattened. The survey Lambda has no date parameters
 * and is deliberately not modified, so the range filter is applied here against
 * each row's submitted_at.
 *
 * q2_reason_tried is MULTI-select, so segment totals can exceed the respondent
 * count. The caption states the respondent count explicitly.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiService from '../../services/api';
import TimeRangeFilter from '../common/TimeRangeFilter';
import { TimeRangeToken, describeRange, resolveRange } from '../common/timeRange';
import { useTimeRangeParam } from '../common/useTimeRangeParam';
import {
  NOT_GIVEN_KEY,
  NOT_GIVEN_LABEL,
  SURVEY_AGE_LABELS,
  SURVEY_AGE_ORDER,
  SURVEY_THEMES,
  themeKeyFor,
} from '../common/onboardingThemes';
import { SurveyResponseRow } from './types';

type ChartRow = { name: string } & Record<string, string | number>;

/** Splits the ' | '-joined multi-select cell the Lambda's flatten() produces. */
function splitValues(cell: unknown): string[] {
  if (cell === undefined || cell === null) return [];
  return String(cell)
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Blank and prefer_not_to_say both occur. They are bucketed into an explicit
 * "Not given" column rather than dropped, so the totals stay honest.
 */
function ageBucket(cell: unknown): string {
  const values = splitValues(cell);
  const value = values[0];
  if (!value || value === 'prefer_not_to_say') return NOT_GIVEN_KEY;
  return SURVEY_AGE_ORDER.includes(value) ? value : NOT_GIVEN_KEY;
}

interface Props {
  /** Which survey's answers to chart. It sits between two panels that follow the
   *  switcher, so without this it silently showed the default campaign's data under
   *  another campaign's heading. */
  campaign?: string | null;
}

const SurveyAgeReasonChart: React.FC<Props> = ({ campaign = null }) => {
  // Prefixed: this page carries other filters, and two range hooks must not collide.
  const [range, setRange] = useTimeRangeParam<TimeRangeToken>({ type: '90d' }, { prefix: 'age' });
  const [rows, setRows] = useState<SurveyResponseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Lambda hard-clamps limit to 500; page through if the survey ever grows.
      const first = await apiService.getSurveyResponses({ limit: 500, campaign: campaign || undefined });
      const collected: SurveyResponseRow[] = first?.responses || [];
      const totalPages = first?.pagination?.total_pages ?? 1;
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await apiService.getSurveyResponses({ page, limit: 500 });
        collected.push(...(next?.responses || []));
      }
      setRows(collected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load survey responses');
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  useEffect(() => {
    load();
  }, [load]);

  const { chartData, respondents } = useMemo(() => {
    const { from, to } = resolveRange(range);
    const inRange = rows.filter((r) => {
      const submitted = new Date(String(r.submitted_at));
      return !isNaN(submitted.getTime()) && submitted >= from && submitted <= to;
    });

    // Only render an age column when something actually lands in it.
    const buckets = [...SURVEY_AGE_ORDER, NOT_GIVEN_KEY];
    const tally = new Map<string, Record<string, number>>();
    buckets.forEach((b) => {
      const seed: Record<string, number> = {};
      SURVEY_THEMES.forEach((t) => {
        seed[t.key] = 0;
      });
      tally.set(b, seed);
    });

    inRange.forEach((row) => {
      const bucket = tally.get(ageBucket(row.q8_age_range));
      if (!bucket) return;
      splitValues(row.q2_reason_tried).forEach((value) => {
        const key = themeKeyFor(SURVEY_THEMES, value);
        if (key) bucket[key] += 1;
      });
    });

    const data: ChartRow[] = buckets
      .filter((b) => Object.values(tally.get(b) || {}).some((n) => n > 0))
      .map((b) => ({
        name: b === NOT_GIVEN_KEY ? NOT_GIVEN_LABEL : SURVEY_AGE_LABELS[b] || b,
        ...(tally.get(b) as Record<string, number>),
      }));

    return { chartData: data, respondents: inRange.length };
  }, [rows, range]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '13px',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Why they tried LifeScribe, by age
        </Typography>
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          onRefresh={load}
          refreshing={loading}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 280, minHeight: 280 }}>
        {loading ? (
          <Box
            sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : chartData.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600 }}
            >
              No survey answers in this range
            </Typography>
            <Typography
              sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: '#6b7280' }}
            >
              Try a wider date range.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {SURVEY_THEMES.map((theme) => (
                <Bar
                  key={theme.key}
                  dataKey={theme.key}
                  name={theme.label}
                  stackId="a"
                  fill={theme.color}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {!loading && (
        <Typography
          sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6b7280', mt: 1.5 }}
        >
          {respondents} {respondents === 1 ? 'respondent' : 'respondents'} in {describeRange(range)}.
          The reason question allows multiple answers, so segments can total more than the
          respondent count.
        </Typography>
      )}
    </Paper>
  );
};

export default SurveyAgeReasonChart;
